import { type InboxItem, InboxItemType } from '@seen/core/models/inbox'
import { useCallback, useMemo } from 'react'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'

export interface InboxListItem {
  key: string
  item: NotificationItem
}

export interface InboxDedupedResult {
  /* Deduped list (one item per resource) */
  items: InboxListItem[]
  /* Map from notification ID to all sibling notifications sharing the same
   * resource key (including the notification itself). Empty array for items
   * without a group key. */
  getSiblings: (item: NotificationItem) => NotificationItem[]
}

/* A bare reference string is not unique across notification families: a
   comment's parentResource is often the very Task that a task notification
   points at, so both would collapse into one group and hide the comment.
   Every key is therefore prefixed with its family. */
function namespacedKey(
  family: string,
  reference: string | undefined
): string | null {
  return reference ? `${family}:${reference}` : null
}

/*
 * Extract a canonical, family-namespaced key for deduplicating notifications
 * about the same underlying resource (Task, Order, Appointment).
 *
 * Returns null for notifications that can't be deduped (legacy comment
 * notifications without parentResource).
 */
export function getGroupKey(
  item: InboxItem,
  legacyDedupeKeys?: Map<string, string>,
  notificationId?: string,
  threadGroupingEnabled = false
): string | null {
  switch (item.type) {
    case InboxItemType.NewComment:
    case InboxItemType.NewCommentReply:
    case InboxItemType.CommentMention:
      /* Thread root first: it identifies the conversation, where parentResource
         identifies whatever the conversation hangs off (often the Patient), which
         collapses unrelated threads into one row. Notifications sent before
         threadRoot shipped fall through to exactly today's behaviour, so nothing
         that groups now stops grouping. */
      return namespacedKey(
        'comment',
        (threadGroupingEnabled ? item.threadRoot?.reference : undefined) ??
          item.parentResource?.reference ??
          (notificationId ? legacyDedupeKeys?.get(notificationId) : undefined)
      )
    default:
      return getStrictGroupKey(item)
  }
}

/*
 * The group key with no fallbacks: the conversation itself, or nothing. This is
 * the only key safe to *archive* by. A comment notification without a proven
 * threadRoot groups for display by parentResource, which lumps every thread on
 * a participant into one row; superseding by that key would archive live,
 * unrelated threads. So such notifications get no key here and are left alone.
 */
export function getStrictGroupKey(item: InboxItem): string | null {
  switch (item.type) {
    case InboxItemType.TaskAssigned:
    case InboxItemType.TaskDue:
    case InboxItemType.TaskNewComment:
      return namespacedKey('task', item.task.reference)
    case InboxItemType.NewComment:
    case InboxItemType.NewCommentReply:
    case InboxItemType.CommentMention:
      return namespacedKey('comment', item.threadRoot?.reference)
    case InboxItemType.NewProposedAppointment:
      return namespacedKey('appointment', item.appointment.reference)
    case InboxItemType.OrderSigned:
      return namespacedKey('order', item.order.reference)
    case InboxItemType.FaxError:
      return namespacedKey('fax', item.communication.reference)
    default:
      return null
  }
}

/*
 * Builds the one group-key definition the inbox uses. Both the deduped list and
 * the sidebar take this same resolver so they cannot disagree: a sidebar that
 * computed its own key silently broke same-group re-selection and made
 * archiveOrDefer misclassify items as "not the open group".
 */
export function useInboxGroupKeyResolver({
  legacyDedupeKeys,
  threadGroupingEnabled,
}: {
  legacyDedupeKeys?: Map<string, string>
  threadGroupingEnabled: boolean
}): (item: NotificationItem) => string | null {
  return useCallback(
    (item: NotificationItem) =>
      getGroupKey(item.data, legacyDedupeKeys, item.id, threadGroupingEnabled),
    [legacyDedupeKeys, threadGroupingEnabled]
  )
}

/*
 * Deduplicates notification items by their underlying resource.
 * Only the latest (first in the already-sorted input) notification per
 * resource is kept. Earlier notifications for the same resource are hidden
 * -- they surface naturally if the latest one is cleared by the backend.
 *
 * Also builds a sibling map so action handlers can cascade operations
 * (archive, read, unread) to all hidden notifications for the same resource.
 */
export function useInboxDedupedItems(
  items: NotificationItem[],
  resolveGroupKey: (item: NotificationItem) => string | null
): InboxDedupedResult {
  return useMemo(() => {
    const seen = new Set<string>()
    const result: InboxListItem[] = []
    /* groupKey -> all NotificationItems with that key */
    const groupMap = new Map<string, NotificationItem[]>()
    /* notificationId -> groupKey (for fast lookup in getSiblings) */
    const itemGroupKey = new Map<string, string>()

    for (const item of items) {
      const key = resolveGroupKey(item)

      if (key) {
        /* Track all items in the group */
        let group = groupMap.get(key)
        if (!group) {
          group = []
          groupMap.set(key, group)
        }
        group.push(item)
        itemGroupKey.set(item.id, key)

        if (seen.has(key)) {
          continue
        }
        seen.add(key)
      }

      result.push({ key: item.id, item })
    }

    const EMPTY: NotificationItem[] = []
    const getSiblings = (item: NotificationItem): NotificationItem[] => {
      const key = itemGroupKey.get(item.id)
      if (!key) {
        return EMPTY
      }
      return groupMap.get(key) ?? EMPTY
    }

    return { items: result, getSiblings }
  }, [items, resolveGroupKey])
}
